import { CheckCircleOutlined, CloseCircleOutlined, ControlTwoTone } from '@ant-design/icons'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Button, Form, message, Popconfirm, Space, Spin } from 'antd'
import { isValidPhoneNumber } from 'libphonenumber-js'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { useSelectedBranch } from '../../../../context/branchContext'
import { useLoginModal } from '../../../../context/loginModalContext'
import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import CustomFieldsDrawer from '../../../common/customFields/CustomFieldsDrawer'
import { getCustomFieldsForSubmit, getInitialCustomFields } from '../../../common/customFields/CustomFieldsHelpers'
import CustomFieldsTypes from '../../../common/customFields/CustomFieldsTypes'
import { confirmInvalidNumber, showConfirm } from '../../appointments/Calendar/calendarHelpers'
import MergePatientButton from './MergePatientButton'
import moment from 'moment'

import PatientCommonFields, { patientRequiredFields } from './PatientCommonFields'
import { DISABLE_PATIENT } from '@dentolize/clinic-mobile/src/shared/store/mutations/companyMutations'
import { ADD_NEW_PATIENT, EDIT_PATIENT } from '@dentolize/clinic-mobile/src/shared/store/mutations/patientMutations'
import { COMPANY_ACTIVE_MESSAGES, GET_FIELDS } from '@dentolize/clinic-mobile/src/shared/store/queries/companyQueries'
import { NEW_PATIENT_ID } from '@dentolize/clinic-mobile/src/shared/store/queries/patientQueries'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import { mainTwoToneColor } from '../../../../variables'

const PatientForm = props => {
  const { selectedItem, handleClose, formVisible } = props

  const [images, setImages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [childDrawerVisible, setChildDrawerVisible] = useState(false)

  const autofocusInputRef = useRef(null)

  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { user } = useUser()
  const client = useApolloClient()
  const navigate = useNavigate()
  const { selectedBranch } = useSelectedBranch()
  const { id } = useParams()
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { data: fieldsData, loading: fieldsLoading } = useQuery(GET_FIELDS, {
    variables: { parent: 'PATIENT' },
    fetchPolicy: 'cache-first',
    skip: !formVisible
  })

  const {
    data: newPatientIdData,
    loading: loadingNewPatientId,
    refetch
  } = useQuery(NEW_PATIENT_ID, {
    fetchPolicy: 'cache-first',
    skip: !selectedItem.new || !formVisible
  })

  const [addNewPatient, { loading }] = useMutation(ADD_NEW_PATIENT)

  const [editPatient, { loading: loadingEdit }] = useMutation(EDIT_PATIENT)

  const [disablePatient, { loading: loadingDisable }] = useMutation(DISABLE_PATIENT)

  const { validateFields, setFields, resetFields, setFieldsValue } = form

  useEffect(() => {
    if (formVisible) {
      resetFields()
      setImages(selectedItem?.images || [])
      if (selectedItem.new) {
        refetch()
          .then(res => {
            setFieldsValue({ referenceId: res.data.newPatientId })
          })
          .catch()
      }
    }
  }, [formVisible, fieldsData])

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  const handleDisable = async () => {
    try {
      await disablePatient({
        variables: {
          patient: selectedItem.id,
          disabled: !selectedItem.disabled
        }
      })
      message.success(selectedItem.disabled ? t('patients.patientEnabled') : t('patients.patientDisabled'))
      if (id) {
        navigate('/patients')
      } else if (props.handleAnimateRow) {
        props.handleAnimateRow(selectedItem.id)
      }
      handleClose()
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  const handleSubmitPatient = key => {
    validateFields()
      .then(async values => {
        const validNumber = isValidPhoneNumber(`${values.suffix}${values.phoneNumber}`)

        if (!validNumber) {
          await confirmInvalidNumber(t)
        }

        let sms
        let err
        try {
          if (user.company.askBeforeSms && selectedItem.new) {
            setLoadingMessages(true)
            const activeMessages = await client.query({
              query: COMPANY_ACTIVE_MESSAGES,
              variables: { branch: selectedBranch }
            })
            setLoadingMessages(false)
            const shouldAsk = !!activeMessages.data.companyActiveMessages.PATIENT_CREATED

            if (shouldAsk) {
              sms = await showConfirm(t)
            }
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
          setLoadingMessages(false)
          err = true
        }

        if (err) return

        if (
          values.family &&
          ((selectedItem.id && values.family?.find(s => s.patient.key === selectedItem.id)) ||
            values.family
              .filter((item, index, self) => self.findIndex(t => t.patient.key === item.patient.key) !== index)
              .map(item => item.patient.key).length)
        ) {
          message.error(t('relations.relationMultiError'))
          return
        }

        const newCustomFields = getCustomFieldsForSubmit(values.customFields)

        const newPatient = {
          ...values,
          generatedID,
          sms,
          lastName: values.middleNames ? `${values.middleNames}${values.lastName ? ` ${values.lastName}` : ''}` : values.lastName,
          phoneNumber: values.phoneNumber ? `${values.suffix || ''}_${values.phoneNumber}` : '',
          phoneNumber2: values.phoneNumber2 ? `${values.suffix2 || ''}_${values.phoneNumber2}` : '',
          phoneNumberE: values.phoneNumberE ? `${values.suffixE || ''}_${values.phoneNumberE}` : '',
          phoneNumber2E: values.phoneNumber2E ? `${values.suffix2E || ''}_${values.phoneNumber2E}` : '',
          branch: values.branch ? values.branch.key : selectedBranch,
          assignedPractitioner: values.assignedPractitioner?.key ?? undefined,
          priceList: values.priceList ? values.priceList.key : undefined,
          referralUser: values.referralUser ? values.referralUser.key : undefined,
          referralPatient: values.referralPatient ? values.referralPatient.key : undefined,
          referralSource: values.referralSource ? values.referralSource.key : undefined,
          referralSubSource: values.referralSubSource ? values.referralSubSource.key : undefined,
          patientTags: values.patientTags?.map(s => s.key) || [],
          insurancePolicy: values.insurancePolicy ? values.insurancePolicy.key : undefined,
          policyClass: values.policyClass ? values.policyClass.key : undefined,
          insurance: values.insurance ? values.insurance.key : undefined,
          insurancePercentage: values.insurancePercentage || values.insurancePercentage === 0 ? values.insurancePercentage : 100,
          insuranceEndDate: values.insuranceEndDate || null,
          insuranceLimit: values.insuranceUnlimited ? 0 : values.insuranceLimit || 0,
          customFields: newCustomFields,
          conversation: selectedItem.conversationId,
          lead: selectedItem.leadId,
          images,
          patientTitle: values.patientTitle || null,
          gender: values.gender || null,
          job: values.job || null,
          relationE: values.relationE || null,
          country: values.country || null,
          family: values.family
            ?.filter(s => !s.id)
            .map(s => ({
              relation: s.relation,
              patient: s.patient.key
            }))
        }

        try {
          if (selectedItem.new) {
            const newPatientData = await addNewPatient({ variables: newPatient })

            message.success(t('common.createdSuccessfully'))

            if (key === 'view') {
              navigate(`/patient/${newPatient.generatedID}`)
              handleClose()
            } else if (key === 'new') {
              await refetch()
              resetFields()
              setImages([])
              handleForceUpdate()
            } else {
              handleClose(newPatientData)
            }
          } else {
            if (values.referralPatient && values.referralPatient.key === selectedItem.id) {
              message.error(t('app.referralError'))
              return
            }
            const variables = { patient: selectedItem.id, ...newPatient, assignNewID: props.assignNewID }

            const editPatientData = await editPatient({ variables })

            if (props.handleAnimateRow) {
              props.handleAnimateRow(selectedItem.id, false)
            }

            message.success(t('common.updatedSuccessfully'))
            handleClose(editPatientData)
          }
        } catch (e) {
          if (e?.message === 'phoneInvalid') {
            return
          }
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const initialCustomFields = getInitialCustomFields(fieldsData, selectedItem, user.company.code)

  // split off only where the form shows the middle names, or those hidden would be dropped from the name when saved
  const hasMiddleName =
    patientRequiredFields({ user, selectedPatient: selectedItem, assignNewID: props.assignNewID }).includes('middleNames') &&
    selectedItem.lastName &&
    selectedItem.lastName.split(' ').length > 1

  const initialValues = {
    ...selectedItem,
    middleNames: hasMiddleName ? selectedItem.lastName.split(' ').slice(0, -1).join(' ') : undefined,
    lastName: hasMiddleName ? selectedItem.lastName.split(' ').pop() : selectedItem.lastName,
    insuranceEndDate: selectedItem.insuranceEndDate ? moment(selectedItem.insuranceEndDate) : null,
    phoneNumber: selectedItem.phoneNumber?.split('_')[1] || '',
    suffix: selectedItem.phoneNumber?.split('_').length > 1 ? selectedItem.phoneNumber.split('_')[0] : user.company.code,
    phoneNumber2: selectedItem.phoneNumber2?.split('_')[1] || '',
    suffix2: selectedItem.phoneNumber2?.split('_').length > 1 ? selectedItem.phoneNumber2.split('_')[0] : user.company.code,
    phoneNumberE: selectedItem.phoneNumberE?.split('_')[1] || '',
    suffixE: selectedItem.phoneNumberE?.split('_').length > 1 ? selectedItem.phoneNumberE.split('_')[0] : user.company.code,
    phoneNumber2E: selectedItem.phoneNumber2E?.split('_')[1] || '',
    suffix2E: selectedItem.phoneNumber2E?.split('_').length > 1 ? selectedItem.phoneNumber2E.split('_')[0] : user.company.code,
    birthDate: selectedItem.birthDate ? moment(selectedItem.birthDate) : undefined,
    createdAt: selectedItem.createdAt ? moment(selectedItem.createdAt) : undefined,
    referenceId: selectedItem.referenceId || newPatientIdData?.newPatientId || undefined,
    customFields: initialCustomFields,
    age: selectedItem.birthDate ? moment().diff(selectedItem.birthDate, 'year') : null,
    family: selectedItem.primaryFamily
      ? selectedItem.primaryFamily.map(s => ({
          ...s,
          patient: {
            key: s.secondary.id,
            label: `${s.secondary.firstName} ${s.secondary.lastName}`
          }
        }))
      : [],
    priceList: selectedItem.priceList
      ? {
          key: selectedItem.priceList.id,
          label: selectedItem.priceList.name
        }
      : undefined,
    insurance: selectedItem.insuranceCompany
      ? {
          key: selectedItem.insuranceCompany.id,
          label: selectedItem.insuranceCompany.name
        }
      : undefined,
    insurancePolicy: selectedItem.insurancePolicy
      ? {
          key: selectedItem.insurancePolicy.id,
          label: selectedItem.insurancePolicy.holderName
        }
      : undefined,
    policyClass: selectedItem.policyClass
      ? {
          key: selectedItem.policyClass.id,
          label: selectedItem.policyClass.name
        }
      : undefined,
    branch: selectedItem.branch
      ? {
          key: selectedItem.branch.id,
          label: selectedItem.branch.name
        }
      : undefined,
    referralUser: selectedItem.referralUser
      ? {
          key: selectedItem.referralUser.id,
          label: selectedItem.referralUser.name
        }
      : undefined,
    referralPatient: selectedItem.referralPatient
      ? {
          key: selectedItem.referralPatient.id,
          label: `${selectedItem.referralPatient.firstName} ${selectedItem.referralPatient.lastName}`
        }
      : undefined,
    referralSource: selectedItem.referralSource
      ? {
          key: selectedItem.referralSource.id,
          label: selectedItem.referralSource.name
        }
      : undefined,
    referralSubSource: selectedItem.referralSubSource
      ? {
          key: selectedItem.referralSubSource.id,
          label: selectedItem.referralSubSource.name
        }
      : undefined,
    patientTags: selectedItem.patientTags
      ? selectedItem.patientTags.map(tag => ({
          key: tag.id,
          label: tag.name
        }))
      : [],
    assignedPractitioner: selectedItem.doctor
      ? {
          key: selectedItem.doctor.id,
          label: selectedItem.doctor.name
        }
      : undefined,
    country: selectedItem.country || user.company.country,
    nationality: selectedItem.nationality || user.company.country,
    patientTitle: selectedItem.title || undefined,
    patientDetails: selectedItem.details || undefined,
    identifierType: selectedItem.identifierType || 'national_id',
    fullDiscount: selectedItem.policyClass
      ? selectedItem.policyClass.insurancePercentage === 100
      : selectedItem.insurancePercentage === 100,
    maxInsuranceLimit: selectedItem.policyClass ? selectedItem.policyClass.maxLimit : undefined,
    insuranceLimit: selectedItem.policyClass ? selectedItem.policyClass.insuranceLimit : selectedItem.insuranceLimit,
    insuranceUnlimited: selectedItem.policyClass ? selectedItem.policyClass.insuranceUnlimited : selectedItem.insuranceUnlimited,
    insurancePercentage: selectedItem.policyClass
      ? selectedItem.policyClass.insurancePercentage
      : selectedItem.insurancePercentage
  }

  return (
    <StyledDrawer
      title={
        selectedItem.new
          ? t('patients.createNewPatient')
          : `${t('patients.editPatient')} - ${selectedItem?.firstName || ''} ${selectedItem?.lastName || ''}`
      }
      extra={
        <Button onClick={() => setChildDrawerVisible(!childDrawerVisible)} type="link">
          <ControlTwoTone twoToneColor={mainTwoToneColor} /> {t('common.customise')}
        </Button>
      }
      open={formVisible}
      onClose={handleClose}
      afterOpenChange={visible => visible && autofocusInputRef.current?.focus()}
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && selectedItem.type !== 2 ? (
            <Space>
              <Popconfirm
                title={selectedItem.disabled ? t('patients.sureEnable') : t('patients.sureDisable')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleDisable}
                disabled={!user.permissions.disablePatient || loading || loadingEdit}
                okButtonProps={{ 'data-testid': 'form-disable-confirm' }}
              >
                <Button
                  loading={loadingDisable}
                  disabled={!user.permissions.disablePatient || loading || loadingEdit}
                  className={selectedItem.disabled ? 'successBtn' : 'dangerBtn'}
                  icon={selectedItem.disabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  data-testid="form-disable"
                >
                  {selectedItem.disabled ? t('common.enable') : t('support.archive')}
                </Button>
              </Popconfirm>
              <MergePatientButton patient={selectedItem} handleAnimateRow={props.handleAnimateRow} handleClose={handleClose} />
            </Space>
          ) : (
            <CreateNewItemExtraButtons formName="PatientForm" handleSubmit={handleSubmitPatient} disabled={loading} />
          )}
          <Button
            data-testid="form-submit"
            loading={loading || loadingEdit}
            htmlType="submit"
            type="primary"
            form={`${generatedID}PatientForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <CustomFieldsDrawer
        parent="PATIENT"
        setChildDrawerVisible={setChildDrawerVisible}
        childDrawerVisible={childDrawerVisible}
        fieldsData={fieldsData || {}}
        fieldsLoading={fieldsLoading}
        parentSetFields={setFields}
      />
      <Spin spinning={loading || loadingEdit || loadingMessages || loadingNewPatientId}>
        <Form
          onFinish={handleSubmitPatient}
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}PatientForm`}
          initialValues={initialValues}
        >
          <PatientCommonFields
            generatedID={generatedID}
            autofocusInputRef={autofocusInputRef}
            form={form}
            selectedPatient={selectedItem}
            formVisible={formVisible}
            images={images}
            setImages={setImages}
            onReset={handleClose}
            assignNewID={props.assignNewID}
          />
          <CustomFieldsTypes fieldsData={fieldsData || {}} />
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

PatientForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func,
  assignNewID: PropTypes.bool
}

export default React.memo(PatientForm, (prevProps, nextProps) => {
  return prevProps.formVisible === nextProps.formVisible
})
